import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import { useParams } from 'react-router-dom';
import { appPaths } from 'consts/paths/paths';
import { DietAddEditForm } from 'components/templates';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { DietId, DietsFormFields, DietDTO } from 'types';
import { useEditForm } from 'hooks/useEditForm/useEditForm';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import { ErrorDisplay } from 'components/molecules';
import { parseDietsFormFieldsIntoAddUpdateDietDTO } from 'utils/parsers/diets/parseDietsFormFieldsIntoAddUpdateDietDTO';
import { parseDietDTOIntoDietsFormFields } from 'utils/parsers/diets/parseDietDTOIntoDietsFormFields';
import { settingsTexts } from 'consts/text';
import { useNavigateWithState } from 'hooks/useNavigateWithState/useNavigateWithState';
import { useCurrentInstitution } from 'jotaiAtoms/currentInstitution';
import { useState } from 'react';

export const DietsEditPage = () => {
    const { form, setFormData } = useEditForm<DietsFormFields>();
    const { statefulNavigate } = useNavigateWithState();
    const { displaySuccess } = useSimpleNotification();
    const currentInstitution = useCurrentInstitution();
    const [dietData, setDietData] = useState<DietDTO | null>(null);

    const { dietId: dietIdParam } = useParams<{ dietId: DietId }>();
    const dietId = dietIdParam || ('' as DietId);

    const onSuccess = () => {
        displaySuccess(settingsTexts.diets.successNotifications.dietEdited);
        statefulNavigate(appPaths.app.settings.diets.base, { replace: true });
    };

    const { isFetching: isDietDataFetching, isError: isDietDataError } = useAppQuery(
        'DIET',
        [dietId],
        () => StaffHTTPService.diets.getDiet(dietId),
        {
            onSuccess: (data) => {
                setFormData(parseDietDTOIntoDietsFormFields(data));
                setDietData(data);
            },
            refetchOnWindowFocus: false,
        },
    );

    const { mutate, error, isLoading } = useAppMutation(StaffHTTPService.diets.updateDiet, {
        key: ['UPDATE_DIET'],
        onSuccess,
        invalidateQueryKeys: [['DIETS'], ['DIET', dietId]],
    });

    const onFinish = (dd: DietsFormFields) => {
        const parsedUpdateDietDTO = parseDietsFormFieldsIntoAddUpdateDietDTO(
            dd,
            currentInstitution.id,
        );
        mutate({ dietId, updateDietDTO: parsedUpdateDietDTO });
    };

    if (isDietDataError) {
        return <ErrorDisplay />;
    }

    return (
        <DietAddEditForm
            type="edit"
            axiosError={error}
            isLoading={isDietDataFetching || isLoading}
            onFinish={onFinish}
            form={form}
            dietData={dietData || undefined}
        />
    );
};
